import React, { useState, useContext, useEffect } from "react";
import { toast } from "react-toastify";
import { format, parseISO } from "date-fns";

import { styled } from "@mui/material/styles";
import { green, red, orange } from "@mui/material/colors";
import {
  Button,
  IconButton,
  Paper,
  Typography,
  CircularProgress,
  Chip,
  Box,
  Card,
  CardContent,
  Grid,
} from "@mui/material";
import {
  Edit,
  Delete,
  Add,
  CheckCircle,
  Error,
  Warning,
  QrCode,
  Api,
  PhoneAndroid,
  Refresh,
  PlayArrow,
  Stop,
} from "@mui/icons-material";

// Componentes do CallCenter
import MainContainer from "../../../components/MainContainer";
import MainHeader from "../../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../../components/MainHeaderButtonsWrapper";
import Title from "../../../components/Title";
import ConfirmationModal from "../../../components/ConfirmationModal";
import ConnectionTypeModal from "../../../components/ConnectionTypeModal";
import OfficialApiModal from "../../../components/OfficialApiModal";
import UnofficialConnectionModal from "../../../components/UnofficialConnectionModal/index.js";
import QrcodeModal from "../../../components/QrcodeModal";

import api from "../../../services/api";
import connectionService from "../../../services/connectionService";
import toastError from "../../../errors/toastError";
import { AuthContext } from "../../../contexts/AuthContext";

const WhatsAppConnections = () => {
  const { user } = useContext(AuthContext);
  
  // Estados para conexões
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Estados para modais
  const [connectionTypeModalOpen, setConnectionTypeModalOpen] = useState(false);
  const [officialApiModalOpen, setOfficialApiModalOpen] = useState(false);
  const [unofficialModalOpen, setUnofficialModalOpen] = useState(false);
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  
  // Estados para edição
  const [selectedConnection, setSelectedConnection] = useState(null);
  const [confirmModalInfo, setConfirmModalInfo] = useState({
    action: "",
    title: "",
    message: "",
    connectionId: "",
  });

  // Carregar conexões
  useEffect(() => {
    fetchConnections();
  }, []);

  useEffect(() => {
    const statuses = (connections || []).map(c => String(c?.status || '').toUpperCase());
    const needsPolling = statuses.some(s => s === 'OPENING' || s === 'QRCODE' || s === 'PENDING');
    if (!needsPolling) return;
    const id = setInterval(() => {
      fetchConnections();
    }, 4000);
    return () => clearInterval(id);
  }, [connections]);

  const fetchConnections = async () => {
    setLoading(true);
    try {
      // Usa o service para normalizar diferentes formatos de resposta
      const list = await connectionService.getConnections();
      const normalized = Array.isArray(list)
        ? list
        : (Array.isArray(list?.data)
            ? list.data
            : (Array.isArray(list?.connections)
                ? list.connections
                : (Array.isArray(list?.whatsapps)
                    ? list.whatsapps
                    : [])));
      // Remove duplicados por `id`
      const unique = Array.isArray(normalized)
        ? Array.from(new Map(normalized.map(c => [c.id, c])).values())
        : [];
      setConnections(unique);
    } catch (err) {
      toastError(err);
      setConnections([]);
    } finally {
      setLoading(false);
    }
  };

  // Handlers para modais
  const handleOpenConnectionTypeModal = () => {
    setConnectionTypeModalOpen(true);
  };

  const handleCloseConnectionTypeModal = () => {
    setConnectionTypeModalOpen(false);
  };

  const handleConnectionTypeSelect = (type) => {
    setConnectionTypeModalOpen(false);
    if (type === "official") {
      setOfficialApiModalOpen(true);
    } else {
      setUnofficialModalOpen(true);
    }
  };

  const handleEditConnection = (connection) => {
    setSelectedConnection(connection);
    if (connection.type === "official") {
      setOfficialApiModalOpen(true);
    } else {
      setUnofficialModalOpen(true);
    }
  };

  const handleDeleteConnection = (connectionId) => {
    setConfirmModalInfo({
      action: "delete",
      title: "Excluir Conexão",
      message: "Tem certeza que deseja excluir esta conexão? Esta ação não pode ser desfeita.",
      connectionId: connectionId,
    });
    setConfirmModalOpen(true);
  };

  const handleTestConnection = async (connectionId) => {
    try {
      const resp = await api.get(`/api/whatsapp-connections/${connectionId}/status/check`);
      const status = resp?.currentStatus || resp?.realStatus || resp?.status || null;
      const lastDisconnect = resp?.lastDisconnect || null;
      if (status === 'CONNECTED') {
        toast.success("Conexão está CONNECTED.");
      } else if (status) {
        if (lastDisconnect?.message || lastDisconnect?.statusCode) {
          const reason = lastDisconnect?.message
            ? String(lastDisconnect.message)
            : `Código: ${String(lastDisconnect.statusCode)}`;
          toast.info(`Status atual: ${status} · Motivo: ${reason}`);
        } else {
          toast.info(`Status atual: ${status}`);
        }
      } else {
        toast.info("Status verificado.");
      }
    } catch (err) {
      toastError(err);
    }
  };

  const handleStartConnection = async (connectionId) => {
    try {
      await api.post(`/api/whatsapp-connections/${connectionId}/start`);
      toast.success("Conexão iniciada!");
      fetchConnections();
    } catch (err) {
      toastError(err);
    }
  };

  const handleStopConnection = async (connectionId) => {
    try {
      await api.post(`/api/whatsapp-connections/${connectionId}/stop`);
      toast.success("Conexão interrompida!");
      fetchConnections();
    } catch (err) {
      toastError(err);
    }
  };

  const handleOpenQrModal = (connection) => {
    setSelectedConnection(connection);
    setQrModalOpen(true);
  };

  const handleConfirmAction = async () => {
    if (confirmModalInfo.action === "delete") {
      try {
        await connectionService.deleteConnection(confirmModalInfo.connectionId);
        toast.success("Conexão excluída com sucesso!");
        fetchConnections();
      } catch (err) {
        toastError(err);
      }
    }
    setConfirmModalOpen(false);
  };

  // Add styled components after the imports
  const StyledComponents = {
    connectedChip: styled(Chip)(({ theme }) => ({
      backgroundColor: green[100],
      color: green[800],
      '& .MuiChip-icon': {
        color: green[600]
      }
    })),
    
    disconnectedChip: styled(Chip)(({ theme }) => ({
      backgroundColor: red[100],
      color: red[800],
      '& .MuiChip-icon': {
        color: red[600]
      }
    })),
    
    pendingChip: styled(Chip)(({ theme }) => ({
      backgroundColor: orange[100],
      color: orange[800],
      '& .MuiChip-icon': {
        color: orange[600]
      }
    })),
    
    statusChip: styled(Chip)(({ theme }) => ({
      fontWeight: 'bold'
    })),
    
    ActionButtons: styled(Box)(({ theme }) => ({
      display: 'flex',
      gap: theme.spacing(1),
      alignItems: 'center'
    })),
    
    MainPaper: styled(Paper)(({ theme }) => ({
      flex: 1,
      padding: theme.spacing(2),
      overflowY: 'auto'
    })),
    
    ConnectionCard: styled(Card)(({ theme }) => ({
      marginBottom: theme.spacing(1),
      '&:hover': {
        boxShadow: theme.shadows[4]
      }
    })),
    
    ConnectionInfo: styled(Box)(({ theme }) => ({
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(2)
    })),
    
    typeIcon: styled(Box)(({ theme }) => ({
      fontSize: '2rem'
    }))
  };

  const getStatusChip = (status) => {
    const normalizeStatus = (s) => {
      if (!s) return 'DISCONNECTED';
      const raw = String(s);
      if (raw === 'qrcode') return 'QRCODE';
      const up = raw.toUpperCase();
      if (up === 'OPENING') return 'CONNECTING';
      if (up === 'connected' || raw === 'connected') return 'CONNECTED';
      if (up === 'disconnected' || raw === 'disconnected') return 'DISCONNECTED';
      return up;
    };

    const norm = normalizeStatus(status);
    const statusConfig = {
      CONNECTED: { label: "Conectado", Component: StyledComponents.connectedChip, icon: <CheckCircle /> },
      DISCONNECTED: { label: "Desconectado", Component: StyledComponents.disconnectedChip, icon: <Error /> },
      QRCODE: { label: "Aguardando QR", Component: StyledComponents.pendingChip, icon: <QrCode /> },
      CONNECTING: { label: "Conectando", Component: StyledComponents.pendingChip, icon: <Warning /> },
    };
  
    const config = statusConfig[norm] || statusConfig.DISCONNECTED;
    const ChipComponent = config.Component;
  
    return (
      <ChipComponent
        icon={config.icon}
        label={config.label}
        size="small"
      />
    );
  };

  // Helper para normalizar status fora dos escopos locais
  const normalizeStatusForSave = (s) => {
    if (!s) return 'DISCONNECTED';
    const raw = String(s);
    if (raw === 'qrcode') return 'QRCODE';
    const up = raw.toUpperCase();
    if (up === 'OPENING') return 'CONNECTING';
    if (up === 'connected' || raw === 'connected') return 'CONNECTED';
    if (up === 'disconnected' || raw === 'disconnected') return 'DISCONNECTED';
    return up;
  };

  const renderActionButtons = (connection) => {
    const normalizeStatus = (s) => {
      if (!s) return 'DISCONNECTED';
      const raw = String(s);
      if (raw === 'qrcode') return 'QRCODE';
      const up = raw.toUpperCase();
      if (up === 'OPENING') return 'CONNECTING';
      if (up === 'connected' || raw === 'connected') return 'CONNECTED';
      if (up === 'disconnected' || raw === 'disconnected') return 'DISCONNECTED';
      return up;
    };
    const status = normalizeStatus(connection.status);
    return (
      <StyledComponents.ActionButtons>
        {connection.type === "unofficial" && (status === "QRCODE" || status === "CONNECTING" || !!connection.qrcode) && (
          <Button
            size="small"
            variant="contained"
            color="primary"
            startIcon={<QrCode />}
            onClick={() => handleOpenQrModal(connection)}
          >
            QR / Teste
          </Button>
        )}
        
        {status === "DISCONNECTED" && (
          <Button
            size="small"
            variant="outlined"
            color="primary"
            startIcon={<PlayArrow />}
            onClick={() => handleStartConnection(connection.id)}
          >
            Conectar
          </Button>
        )}
        
        {status === "CONNECTED" && (
          <Button
            size="small"
            variant="outlined"
            color="secondary"
            startIcon={<Stop />}
            onClick={() => handleStopConnection(connection.id)}
          >
            Desconectar
          </Button>
        )}
        
        <Button
          size="small"
          variant="outlined"
          startIcon={<Refresh />}
          onClick={() => handleTestConnection(connection.id)}
        >
          Testar
        </Button>
        
        <IconButton
          size="small"
          onClick={() => handleEditConnection(connection)}
          color="primary"
        >
          <Edit />
        </IconButton>
        
        <IconButton
          size="small"
          onClick={() => handleDeleteConnection(connection.id)}
          color="secondary"
        >
          <Delete />
        </IconButton>
      </StyledComponents.ActionButtons>
    );
  };

  return (
    <MainContainer>
      {/* Modais */}
      <ConnectionTypeModal
        open={connectionTypeModalOpen}
        onClose={handleCloseConnectionTypeModal}
        onSelect={handleConnectionTypeSelect}
      />
      
      <OfficialApiModal
        open={officialApiModalOpen}
        onClose={() => {
          setOfficialApiModalOpen(false);
          setSelectedConnection(null);
        }}
        connection={selectedConnection}
        onSave={() => {
          fetchConnections();
          setOfficialApiModalOpen(false);
          setSelectedConnection(null);
        }}
      />
      
      <UnofficialConnectionModal
        open={unofficialModalOpen}
        onClose={() => {
          setUnofficialModalOpen(false);
          setSelectedConnection(null);
        }}
        connection={selectedConnection}
        currentUser={user}
        onSave={(savedConnection) => {
          fetchConnections();
          setUnofficialModalOpen(false);
          // Abrir modal de QR somente quando o status indicar necessidade de pareamento
          if (savedConnection && savedConnection.id) {
            const status = normalizeStatusForSave(savedConnection.status);
            const shouldOpenQr = savedConnection.type === 'unofficial' && (status === 'QRCODE' || status === 'CONNECTING' || !!savedConnection.qrcode);
            if (shouldOpenQr) {
              setSelectedConnection(savedConnection);
              setQrModalOpen(true);
            } else {
              setSelectedConnection(null);
            }
          } else {
            setSelectedConnection(null);
          }
        }}
      />
      
      <QrcodeModal
        open={qrModalOpen}
        onClose={() => setQrModalOpen(false)}
        whatsAppId={selectedConnection?.id}
      />
      
      <ConfirmationModal
        title={confirmModalInfo.title}
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={handleConfirmAction}
      >
        {confirmModalInfo.message}
      </ConfirmationModal>

      {/* Header */}
      <MainHeader>
        <Title>Conexões WhatsApp</Title>
        <MainHeaderButtonsWrapper>
          <Button
            variant="contained"
            color="primary"
            startIcon={<Add />}
            onClick={handleOpenConnectionTypeModal}
          >
            Adicionar Conexão
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      {/* Conteúdo Principal */}
      <StyledComponents.MainPaper variant="outlined">
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : !Array.isArray(connections) || connections.length === 0 ? (
          <Box textAlign="center" p={4}>
            <Typography variant="h6" color="textSecondary">
              Nenhuma conexão configurada
            </Typography>
            <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
              Clique em "Adicionar Conexão" para começar
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={2} style={{ width: '100%', margin: 0 }}>
            {(Array.isArray(connections) ? connections : []).map((connection) => (
              <Grid item xs={12} key={connection.id} style={{ width: '100%', maxWidth: '100%' }}>
                <StyledComponents.ConnectionCard>
                  <CardContent style={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
                    <Grid container spacing={2} alignItems="center" style={{ width: '100%', margin: 0 }}>
                      {/* Informações da Conexão */}
                      <Grid item xs={12} md={4} style={{ maxWidth: '100%' }}>
                        <StyledComponents.ConnectionInfo>
                          {connection.type === "official" ? (
                            <Api color="primary" />
                          ) : (
                            <PhoneAndroid color="secondary" />
                          )}
                          <div style={{ minWidth: 0, flex: 1 }}>
                            <Typography variant="h6" noWrap>{connection.name}</Typography>
                            <Typography variant="body2" color="textSecondary" noWrap>
                              {connection.type === "official" ? "API Oficial" : "Conexão Não Oficial"}
                            </Typography>
                            <Typography variant="caption" color="textSecondary" noWrap>
                              Tipo: {connection.messageCategory === 'billing' ? 'Cobrança' : connection.messageCategory === 'otp' ? 'OTP' : 'Serviço'}
                            </Typography>
                          </div>
                        </StyledComponents.ConnectionInfo>
                      </Grid>
                      
                      {/* Status */}
                      <Grid item xs={12} md={2} style={{ maxWidth: '100%' }}>
                        {getStatusChip(connection.status)}
                        {(String(connection?.status || '').toUpperCase() !== 'CONNECTED') && (connection?.lastDisconnect?.message || connection?.lastDisconnect?.statusCode) && (
                          <Typography variant="caption" color="textSecondary" display="block" style={{ marginTop: 6 }} noWrap>
                            Motivo: {connection?.lastDisconnect?.message ? String(connection.lastDisconnect.message) : `Código: ${String(connection.lastDisconnect.statusCode)}`}
                          </Typography>
                        )}
                      </Grid>
                      
                      {/* Última Atualização */}
                      <Grid item xs={12} md={3} style={{ maxWidth: '100%' }}>
                        <Typography variant="body2" color="textSecondary">
                          Última atualização:
                        </Typography>
                        <Typography variant="body2" noWrap>
                          {connection.updatedAt
                            ? format(parseISO(connection.updatedAt), "dd/MM/yyyy HH:mm")
                            : "Nunca"}
                        </Typography>
                      </Grid>
                      
                      {/* Ações */}
                      <Grid item xs={12} md={3} style={{ maxWidth: '100%' }}>
                        <StyledComponents.ActionButtons>
                          {renderActionButtons(connection)}
                        </StyledComponents.ActionButtons>
                      </Grid>
                    </Grid>
                  </CardContent>
                </StyledComponents.ConnectionCard>
              </Grid>
            ))}
          </Grid>
        )}
      </StyledComponents.MainPaper>
    </MainContainer>
  );
};

export default WhatsAppConnections;
